import { FC, useCallback, useRef, useState } from 'react';
import { addDays, addMonths, endOfDay, isEqual, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DateTimePickerField from 'components/forms/dateTimePickerField/DateTimePickerField';
import Button from 'components/button/Button';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { useFormContext } from 'react-hook-form';
import { ScreenAndProtectDetailsFormSchemaType } from '../ScreenAndProtectDetails.schema';
import { StyledStartDateField } from './StartDateField.styled';

const StartDateField: FC = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext<ScreenAndProtectDetailsFormSchemaType>();
  const startDate = watch('startDate');
  const minStartDate = addDays(startOfDay(new Date()), 2);
  const maxStartDate = addMonths(endOfDay(new Date()), 6);
  const [isOpen, setOpen] = useState(false);
  const startDateTime = startDate?.getTime();
  const prevStartDateRef = useRef(startDateTime);
  const containerRef = useRef<HTMLDivElement>(null);

  const onToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const onChangeDate = useCallback((date: Date | null) => {
    if (date && !isEqual(date.getTime(), prevStartDateRef.current)) {
      prevStartDateRef.current = date.getTime();
      setOpen(false);
    }
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent | FocusEvent) => {
      const isInsideDatePickerPopper = document
        .querySelector('.date-time-picker-popper')
        ?.contains(event.target as Node);

      if (!isInsideDatePickerPopper) {
        setOpen(false);
      }
    },
    [],
  );

  useOnClickOutside(containerRef, handleClickOutside);

  return (
    <StyledStartDateField ref={containerRef}>
      <span>
        {t(
          'pageScreenAndProtect.detailsPage.sections.protectionDetails.startDateValue',
          { date: startDate },
        )}
      </span>
      <Button
        bsStyle="link"
        onClick={onToggle}
        data-testid="toggle-start-date-picker-button"
      >
        {t(
          'pageScreenAndProtect.detailsPage.sections.protectionDetails.startDateLabel',
        )}
      </Button>
      <DateTimePickerField
        open={isOpen}
        name="startDate"
        containerClassName="start-date-container"
        colSmInput={12}
        colSmLabel={0}
        required
        minDate={minStartDate}
        maxDate={maxStartDate}
        onChangeHandler={onChangeDate}
      />
    </StyledStartDateField>
  );
};

export default StartDateField;
